import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";
import { DateRangePicker, DateRange } from "mui-daterange-picker";
import swal from "sweetalert";
import PushSendModal from "./PushSendModal";
import { CHANNELS, listSends, cancelSend, executeSend, ListSendsRow } from "services/push-automation.service";
import { getGroupsListByUser } from "services/programs.service";
import useDebouncedValue from "hooks/useDebouncedValue";

// YYYY-MM-DD a partir de los componentes locales de la fecha, igual que dayjs().format("YYYY-MM-DD")
// en el resto de la pantalla — usar toISOString() correría el dia si el offset local cae del otro
// lado de medianoche UTC.
function formatDateOnly(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const STATUSES = ["pending", "sending", "sent", "cancelled"];

function buildColumns(
  onView: (row: ListSendsRow) => void,
  onCancel: (row: ListSendsRow) => void,
  onExecute: (row: ListSendsRow, dry: boolean) => void,
): GridColDef<ListSendsRow>[] {
  return [
    { field: "id", headerName: "ID", width: 80 },
    { field: "date_to_send", headerName: "Fecha", flex: 1 },
    { field: "channel", headerName: "Canal", flex: 1.2 },
    {
      field: "program_ids",
      headerName: "Programas",
      flex: 1,
      valueGetter: (_value, row) => (row.program_ids ? row.program_ids.join(", ") : "N/A"),
    },
    {
      field: "templates",
      headerName: "Templates",
      flex: 1,
      sortable: false,
      valueGetter: (_value, row) => (row.templates.length ? row.templates.join(", ") : row.push_name ?? "N/A"),
    },
    {
      field: "status",
      headerName: "Estado",
      flex: 0.9,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5} alignItems="center">
          <Typography variant="body2">{params.row.status}</Typography>
          {params.row.report?.dry && <Chip label="DRY" size="small" color="info" />}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="Ver configuración y reporte">
            <Button
              size="small"
              variant="outlined"
              sx={{ minWidth: 0, px: 1 }}
              onClick={(e) => {
                e.stopPropagation();
                onView(params.row);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </Button>
          </Tooltip>
          <Tooltip title="Enviar ahora (prueba, dry run)">
            <Button
              size="small"
              variant="outlined"
              color="primary"
              sx={{ minWidth: 0, px: 1 }}
              onClick={(e) => {
                e.stopPropagation();
                onExecute(params.row, true);
              }}
            >
              <PlayArrowIcon fontSize="small" />
            </Button>
          </Tooltip>
          <Tooltip title="Enviar ahora (real)">
            <Button
              size="small"
              variant="outlined"
              color="warning"
              sx={{ minWidth: 0, px: 1 }}
              onClick={(e) => {
                e.stopPropagation();
                onExecute(params.row, false);
              }}
            >
              <SendIcon fontSize="small" />
            </Button>
          </Tooltip>
          {params.row.status === "pending" && (
            <Tooltip title="Cancelar envío">
              <Button
                size="small"
                variant="outlined"
                color="error"
                sx={{ minWidth: 0, px: 1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onCancel(params.row);
                }}
              >
                <CloseIcon fontSize="small" />
              </Button>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];
}

function PushNotificationsPage() {
  const history = useHistory();
  const [rows, setRows] = useState<ListSendsRow[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);
  const [matchingProgramIds, setMatchingProgramIds] = useState<string | undefined>(undefined);
  const [channel, setChannel] = useState("");
  const [status, setStatus] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>({});
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "date_to_send", sort: "desc" }]);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Buscar por nombre de programa: resuelve el nombre contra programs (mismo
  // /user_group/?search= que ya usa el multiselect del formulario) y filtra el
  // listado por los ids de grupo que matcheen — program_ids en la DB es solo el id
  // numerico, no el nombre, asi que no se puede filtrar por nombre directo ahi.
  useEffect(() => {
    if (!debouncedSearch.trim()) {
      setMatchingProgramIds(undefined);
      return;
    }
    let cancelled = false;
    getGroupsListByUser(1, debouncedSearch.trim()).then(({ data }) => {
      if (cancelled) return;
      const ids = data.results.map((group: any) => group.id).join(",");
      setMatchingProgramIds(ids || "0"); // "0": sin coincidencias, ningun grupo real usa ese id
    });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch]);

  const fetchSends = useCallback(async () => {
    const sort = sortModel[0];
    const response = await listSends({
      ...(matchingProgramIds ? { program_ids: matchingProgramIds } : {}),
      ...(channel ? { channel } : {}),
      ...(status ? { status } : {}),
      ...(dateRange.startDate ? { date_from: formatDateOnly(dateRange.startDate) } : {}),
      ...(dateRange.endDate ? { date_to: formatDateOnly(dateRange.endDate) } : {}),
      ...(sort ? { order_by: sort.field, order_dir: (sort.sort ?? "desc") as "asc" | "desc" } : {}),
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
    });
    setRows(response.data.rows);
    setTotal(response.data.total);
  }, [matchingProgramIds, channel, status, dateRange, sortModel, paginationModel]);

  useEffect(() => {
    fetchSends();
  }, [fetchSends]);

  const handleView = useCallback(
    (row: ListSendsRow) => {
      history.push(`/admin/push/calendar/report/${row.id}`);
    },
    [history],
  );

  const handleCancel = useCallback(
    async (row: ListSendsRow) => {
      const willCancel = await swal({
        title: "Cancelar envío",
        text: `¿Seguro que quieres cancelar el envío #${row.id}?`,
        icon: "warning",
        buttons: ["No", "Sí, cancelar"],
        dangerMode: true,
      });
      if (!willCancel) return;
      try {
        await cancelSend(row.id);
        swal("Afinidata", "Envío cancelado correctamente", "success");
        fetchSends();
      } catch (err: any) {
        const message = err?.response?.data?.error || "No se pudo cancelar el envío";
        swal("Afinidata", message, "error");
      }
    },
    [fetchSends],
  );

  const handleExecute = useCallback(
    async (row: ListSendsRow, dry: boolean) => {
      const willExecute = await swal({
        title: dry ? "Enviar ahora (prueba)" : "Enviar ahora (real)",
        text: dry
          ? `¿Ejecutar una prueba (dry run) del envío #${row.id}? Estado actual: ${row.status}. No se enviará nada real.`
          : `¿Ejecutar el envío #${row.id} de verdad? Estado actual: ${row.status}. Esto SÍ envía notificaciones reales.`,
        icon: "warning",
        buttons: ["Cancelar", dry ? "Sí, probar" : "Sí, enviar"],
        dangerMode: !dry,
      });
      if (!willExecute) return;
      try {
        const { data } = await executeSend(row.id, dry);
        swal("Afinidata", data.message, "success");
        fetchSends();
      } catch (err: any) {
        const message = err?.response?.data?.error || "No se pudo ejecutar el envío";
        swal("Afinidata", message, "error");
      }
    },
    [fetchSends],
  );

  const columns = buildColumns(handleView, handleCancel, handleExecute);

  return (
    <Box>
      <Typography variant="h5" mb={2}>
        Configurar envío de push
      </Typography>
      <Box display="flex" gap={2} mb={2} alignItems="center">
        <TextField
          label="Buscar programa por nombre"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="channel-filter-label">Canal</InputLabel>
          <Select labelId="channel-filter-label" label="Canal" value={channel} onChange={(e) => setChannel(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {CHANNELS.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="status-filter-label">Estado</InputLabel>
          <Select labelId="status-filter-label" label="Estado" value={status} onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box position="relative">
          <Button variant="outlined" size="small" onClick={() => setDateRangeOpen((prev) => !prev)}>
            {dateRange.startDate && dateRange.endDate
              ? `${formatDateOnly(dateRange.startDate)} — ${formatDateOnly(dateRange.endDate)}`
              : "Rango de fechas"}
          </Button>
          {(dateRange.startDate || dateRange.endDate) && (
            <Button size="small" onClick={() => setDateRange({})}>
              Limpiar
            </Button>
          )}
          <DateRangePicker
            open={dateRangeOpen}
            toggle={() => setDateRangeOpen((prev) => !prev)}
            initialDateRange={dateRange}
            onChange={(range) => setDateRange(range)}
          />
        </Box>
        <Box flexGrow={1} />
        <Button variant="contained" onClick={() => setCreateModalOpen(true)}>
          Nuevo envío
        </Button>
      </Box>

      <DataGrid
        rows={rows}
        columns={columns}
        rowCount={total}
        paginationMode="server"
        sortingMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        pageSizeOptions={[10, 20, 50, 100]}
        getRowHeight={() => "auto"}
        rowSpacingType="border"
        autoHeight
        density="comfortable"
        sx={{ "& .MuiDataGrid-cell": { py: 1.5 } }}
        onRowClick={(params) => history.push(`/admin/push/calendar/report/${params.row.id}`)}
      />

      <PushSendModal visible={createModalOpen} close={() => setCreateModalOpen(false)} refresh={fetchSends} />
    </Box>
  );
}

export default PushNotificationsPage;
